import React, { ReactElement, useState } from 'react';
import { Box } from 'rebass/styled-components';

import { SlidePanel, Alert } from 'ui/patterns';
import { Button, Stack, Checkbox, Dropzone, FormLabel } from 'ui/components';

import { useUploadCustomTable } from 'hooks';

import * as Icon from 'assets/icons';

import { showErrorToast, showSuccessToast } from 'utils/showToast';

import { ImportRecordProps } from './importCustomTableTypes';
import { ErrorModal } from 'types/modalTypes';

export default function ImportCustomRecords({
  showPanel,
  togglePanel,
  accountId,
  lookupId,
  title,
}: ImportRecordProps): ReactElement {
  const [file, setFile] = useState<File>();
  const [isForced, setIsForce] = useState(false);

  const { mutate: uploadCustomTable, isLoading: isUploadCustomTableLoading } =
    useUploadCustomTable();

  function onSubmit() {
    if (file && accountId && lookupId)
      uploadCustomTable(
        { account: accountId, lookupId: lookupId, file, isForced },
        {
          onSuccess: () => {
            showSuccessToast({
              message: 'Your changes were successfully saved.',
            });
            togglePanel();
            setFile(undefined);
            setIsForce(false);
          },
          onError: (error: ErrorModal) => {
            showErrorToast({ message: `${error.Errors[0].Message}` });
            setFile(undefined);
            setIsForce(false);
          },
        },
      );
  }

  const addRecordsAction = () => (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          togglePanel();
          setFile(undefined);
          setIsForce(false);
        }}
      >
        Cancel
      </Button>
      <Button
        onClick={onSubmit}
        variant="primary"
        disabled={isUploadCustomTableLoading}
      >
        Save
      </Button>
    </>
  );

  return (
    <>
      <SlidePanel
        title={title ? `Import table - ${title}` : 'Import Table'}
        actions={addRecordsAction()}
        onClose={() => {
          togglePanel();
          setFile(undefined);
          setIsForce(false);
        }}
        open={showPanel}
        size="sm"
      >
        <Stack direction="y" gap={3}>
          <Alert variant="danger" icon={<Icon.ExclamationCircle />}>
            Importing a CSV will overwrite the records in this lookup table.
          </Alert>
          <Alert variant="info" icon={<Icon.Info />}>
            The CSV should include a header row and should have the same number
            of columns as this lookup table. For an example of a properly
            formatted CSV, please use the Export button to export this custom
            lookup to CSV.
          </Alert>
          <Box>
            <FormLabel>Import File</FormLabel>
            <Dropzone
              value={file && file.name}
              isLoading={isUploadCustomTableLoading}
              onChange={(file?: File) => {
                setFile(file);
              }}
            />
          </Box>
          <Checkbox
            key={`key-${title}`}
            label="Force Import"
            name="forceImport"
            checked={isForced}
            handleChange={e => {
              e.stopPropagation();
              setIsForce(!isForced);
            }}
          />
        </Stack>
      </SlidePanel>
    </>
  );
}
